// =============================================================
// Mode splash
// =============================================================
//
// On every visit, full-bleed overlay with two clickable halves —
// yoga on the left, strength on the right. The background is one
// continuous pre-blended photo (`/splash-blended.jpg` + WebP variant),
// so there's no visible stitched join between the two halves. Picking
// either side calls setMode() and fades the overlay out. The splash
// shows on every page load (the user wants the choice surfaced every
// time, not just on the first visit).
//
// The splash honors `prefers-reduced-motion` by skipping the fade
// animation.

import { setMode, type Mode } from './mode';

// Single blended image — ~600 KB JPEG (was 16 MB as the original PNG).
// The two photos are pre-blended at the natural seam so the user sees
// one continuous picture across the two halves. WebP variant is ~170 KB.
const SPLASH_BG = '/splash-blended.jpg';
const YOGA_LOGO = '/logo-yoga.svg';
const STRENGTH_LOGO = '/logo.svg';

/**
 * Preload the splash image. Resolves when the image is decoded (not
 * just fetched — the browser won't paint on a partially-decoded
 * JPEG, so the splash would still tear without the await).
 *
 * Tries WebP first (~170 KB), falls back to JPEG (~600 KB) on error.
 * Idempotent: repeats return the same promise. Call this as early as
 * possible — ideally before the first paint path can reach the
 * splash overlay.
 */
let _preloadPromise: Promise<void> | null = null;
export function preloadSplashImages(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if (_preloadPromise) return _preloadPromise;
  const webp = SPLASH_BG.replace(/\.jpg$/, '.webp');
  _preloadPromise = new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => {
      // WebP failed (older browser) — try the JPEG fallback.
      const fallback = new Image();
      fallback.onload = () => resolve();
      fallback.onerror = () => resolve();   // never block the splash
      fallback.src = SPLASH_BG;
    };
    img.src = webp;
  });
  return _preloadPromise;
}

/** Should we show the splash on this load? Always — every visit. */
export function shouldShowSplash(): boolean {
  return true;
}

/**
 * Mount the splash overlay. Resolves once the user picks a mode (and the
 * splash fade-out completes) — with the chosen mode as the result.
 */
export function mountSplash(): Promise<Mode> {
  // Two-stage: wait for the JPEGs to decode, then build the overlay.
  // The async-executor pattern (`new Promise(async ...)`) is spec-banned
  // (the executor's promise is ignored and any rejection becomes an
  // unhandled error) and historically emits console noise in Firefox
  // that masks other errors. Splitting into .then() is portable and
  // gives us a normal Promise chain.
  return preloadSplashImages().then(() => new Promise<Mode>((resolve) => {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ----- Build DOM (programmatic — no innerHTML with user input) -----
    const root = document.createElement('div');
    root.className = 'splash';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Choose mode');

    const bg = document.createElement('div');
    bg.className = 'splash__bg';
    // The image is one continuous pre-blended photo (~170 KB WebP /
    // ~600 KB JPEG). Using a single image — instead of two halves with
    // a join — means there's no visible seam at the centre of the
    // splash to begin with. Preloader probes WebP support; if the
    // browser rejects it, we swap to the JPEG inline.
    const webp = SPLASH_BG.replace(/\.jpg$/, '.webp');
    bg.style.backgroundImage = `url(${webp})`;
    const probe = new Image();
    probe.onerror = () => { bg.style.backgroundImage = `url(${SPLASH_BG})`; };
    probe.src = webp;
    root.appendChild(bg);

    const grid = document.createElement('div');
    grid.className = 'splash__grid';
    root.appendChild(grid);

    const makeZone = (mode: Mode, logoSrc: string, labelText: string, ariaLabel: string) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `splash__zone splash__zone--${mode}`;
      btn.dataset.mode = mode;
      btn.setAttribute('aria-label', ariaLabel);

      const overlay = document.createElement('div');
      overlay.className = 'splash__zone-overlay';
      btn.appendChild(overlay);

      const logo = document.createElement('img');
      logo.className = 'splash__zone-logo';
      logo.src = logoSrc;
      logo.alt = '';
      logo.setAttribute('aria-hidden', 'true');
      logo.loading = 'eager';
      logo.decoding = 'async';
      btn.appendChild(logo);

      const label = document.createElement('span');
      label.className = 'splash__zone-label';
      label.textContent = labelText;
      btn.appendChild(label);

      btn.addEventListener('click', () => choose(mode));
      return btn;
    };

    // Label text is intentionally hard-coded here — splash.yoga / splash.strength
    // i18n keys are also referenced via data-i18n on a hidden mirror element so
    // the existing applyI18n() pass picks them up if the user toggles locale
    // before dismissing the splash. For first paint (the splash has the floor
    // before applyI18n runs), we set plain text in the default locale (hu).
    grid.appendChild(makeZone('yoga',     YOGA_LOGO,     'jóga',     '333 Yoga — belépés'));
    grid.appendChild(makeZone('strength', STRENGTH_LOGO, 'edzés',    '333 Strength — belépés'));

    const title = document.createElement('h1');
    title.className = 'splash__title';
    title.dataset.i18n = 'splash.title';
    title.textContent = 'Melyik 333-at keresed?';
    root.appendChild(title);

    const hint = document.createElement('p');
    hint.className = 'splash__hint';
    hint.dataset.i18n = 'splash.hint';
    hint.textContent = 'Válassz — később a \'333\' gombra kattintva bármikor válthatsz.';
    root.appendChild(hint);

    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'splash__skip';
    skip.dataset.i18n = 'splash.skip';
    skip.textContent = 'Kihagyom';
    skip.addEventListener('click', () => choose('strength'));
    root.appendChild(skip);

    document.body.appendChild(root);

    // Don't programmatically focus a zone on mount — Firefox shows
    // :focus-visible on programmatic .focus(), and the default outset
    // outline bleeds 4px across the centre join between the two halves,
    // producing a 2 px white vertical line right where the user doesn't
    // want one. Mouse users get the hover state as their cue; keyboard
    // users Tab into the splash and land on whichever zone they reach,
    // which is more honest than auto-focusing a default pick.

    function choose(mode: Mode): void {
      setMode(mode);
      document.removeEventListener('keydown', onKey);
      if (reduced) {
        root.remove();
        resolve(mode);
        return;
      }
      root.classList.add('is-leaving');
      const finish = () => {
        root.remove();
        resolve(mode);
      };
      // Wait for the leave animation; fall back to a hard timeout in case
      // animationend doesn't fire (e.g. reduced-motion was toggled mid-anim).
      const fallback = window.setTimeout(finish, 360);
      root.addEventListener('animationend', () => {
        window.clearTimeout(fallback);
        finish();
      }, { once: true });
    }

    const onKey = (e: KeyboardEvent) => {
      const zones = Array.from(root.querySelectorAll<HTMLButtonElement>('.splash__zone'));
      if (e.key === 'Escape') {
        e.preventDefault();
        choose('strength');
        return;
      }
      if (e.key === 'Tab') {
        // Trap focus between the two zones + skip button
        const focusables = [...zones, skip];
        const i = focusables.indexOf(document.activeElement as HTMLButtonElement);
        if (i === -1) return;
        e.preventDefault();
        const next = e.shiftKey
          ? focusables[(i - 1 + focusables.length) % focusables.length]
          : focusables[(i + 1) % focusables.length];
        next.focus();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const next = e.key === 'ArrowRight' ? zones[1] : zones[0];
        next?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
  }));
}
